import { Search as SearchIcon } from '@mui/icons-material';
import { Autocomplete, Box, CircularProgress, InputAdornment, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { User } from '../models/user';
import { useUserSearch } from '../queries/use-user-search';
import { UserItemContent } from './user-list/user-item';

interface UserAutocompleteProps {
  selectedUsers?: User[];
  onUserSelect: (user: User) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  excludeUserIds?: string[];
}

export const UserAutocomplete: React.FC<UserAutocompleteProps> = ({
  selectedUsers = [],
  onUserSelect,
  placeholder,
  label,
  disabled = false,
  excludeUserIds = []
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const effectivePlaceholder = placeholder || t('participant.autocomplete.placeholder');

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(inputValue);
    }, 300);

    return () => clearTimeout(timer);
  }, [inputValue]);

  const { data, isLoading } = useUserSearch(debouncedSearchTerm, !disabled);

  // Filter out already selected users and excluded users
  const selectedUserIds = new Set(selectedUsers.map((u) => u.id));
  const excludedIds = new Set(excludeUserIds);
  const filteredUsers = data?.items.filter((user) => !selectedUserIds.has(user.id) && !excludedIds.has(user.id)) || [];

  const handleSelect = (_event: any, value: User | null) => {
    if (value) {
      onUserSelect(value);
      setInputValue(''); // Clear input after selection
    }
  };

  return (
    <Autocomplete
      options={filteredUsers}
      getOptionLabel={(option) => option.fullName || option.userName || ''}
      loading={isLoading}
      disabled={disabled}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      getOptionKey={(option) => option.id}
      onChange={handleSelect}
      value={null}
      noOptionsText={
        inputValue.length < 3
          ? t('participant.autocomplete.minCharacters')
          : isLoading
            ? t('participant.autocomplete.searching')
            : t('participant.autocomplete.noUsers')
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={effectivePlaceholder}
          size='small'
          slotProps={{
            input: {
              ...params.InputProps,
              startAdornment: (
                <>
                  <InputAdornment position='start'>
                    <SearchIcon fontSize='small' />
                  </InputAdornment>
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {isLoading ? <CircularProgress color='inherit' size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              )
            }
          }}
        />
      )}
      renderOption={(props, option) => {
        return (
          <Box
            component='li'
            {...props}
            key={option.id}
            style={{
              display: 'flex',
              gap: '12px',
              alignItems: 'center',
              padding: '8px 16px'
            }}
          >
            <UserItemContent user={option} showPosition avatarSize={40} />
          </Box>
        );
      }}
      filterOptions={(x) => x} // Disable client-side filtering (use API filtering)
      isOptionEqualToValue={(option, value) => option.id === value.id}
    />
  );
};
